import { AdminPage, AdminPageHeader } from '../../components/layout/AdminPage';
import { MemberPolicyRepair } from './MemberPolicyRepair';
import { PointTypeCatalog } from './PointTypeCatalog';
import { MemberLevelTransferPanel } from './MemberLevelTransferPanel';
/**
 * @page PointsConfigPage
 * @description 會員等級管理頁 — CRUD 等級（名稱、色碼、倍率、基礎計點）
 */

import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { api } from '../../lib/api';
import type { MemberLevelDto } from '@pokerroom/types';

interface LevelFormState {
  code: string;
  displayName: string;
  color: string;
  sortOrder: string;
  basePoints: string;
  multiplier: string;
  isDefault: boolean;
  isZCustomer: boolean;
}

const EMPTY_FORM: LevelFormState = {
  code: '',
  displayName: '',
  color: '#3B82F6',
  sortOrder: '0',
  basePoints: '1',
  multiplier: '1.00',
  isDefault: false,
  isZCustomer: false,
};

export default function PointsConfigPage() {
  const { t: tc } = useTranslation('common');
  const { t } = useTranslation('member');
  const queryClient = useQueryClient();

  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [form, setForm] = useState<LevelFormState>(EMPTY_FORM);
  const [formError, setFormError] = useState('');
  const [deleteConfirm, setDeleteConfirm] = useState<string | null>(null);

  const { data: levels = [], isLoading } = useQuery<MemberLevelDto[]>({
    queryKey: ['member-levels'],
    queryFn: () => api.get<MemberLevelDto[]>('/members/levels'),
  });

  const createMutation = useMutation({
    mutationFn: (data: Record<string, unknown>) => api.post('/members/levels', data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['member-levels'] });
      resetForm();
    },
    onError: (err) => setFormError(err instanceof Error ? err.message : t('createFailed')),
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, data }: { id: string; data: Record<string, unknown> }) =>
      api.patch(`/members/levels/${id}`, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['member-levels'] });
      resetForm();
    },
    onError: (err) => setFormError(err instanceof Error ? err.message : t('updateFailed')),
  });

  const deleteMutation = useMutation({
    mutationFn: (id: string) => api.delete(`/members/levels/${id}`),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['member-levels'] });
      setDeleteConfirm(null);
    },
    onError: (err) => {
      setFormError(err instanceof Error ? err.message : t('deleteFailedLevel'));
      setDeleteConfirm(null);
    },
  });

  function resetForm() {
    setShowForm(false);
    setEditingId(null);
    setForm(EMPTY_FORM);
    setFormError('');
  }

  function startCreate() {
    setEditingId(null);
    setForm(EMPTY_FORM);
    setFormError('');
    setShowForm(true);
  }

  function startEdit(level: MemberLevelDto) {
    setEditingId(level.id);
    setForm({
      code: level.code,
      displayName: level.displayName,
      color: level.color,
      sortOrder: String(level.sortOrder),
      basePoints: level.basePoints == null ? '' : String(level.basePoints),
      multiplier: level.multiplier == null ? '' : String(parseFloat(level.multiplier)),
      isDefault: level.isDefault,
      isZCustomer: level.isZCustomer ?? false,
    });
    setFormError('');
    setShowForm(true);
  }

  function handleSave() {
    if (!form.displayName.trim()) {
      setFormError(t('displayNameEmpty'));
      return;
    }
    const skipPolicy = !!editingId && !form.basePoints.trim() && !form.multiplier.trim();
    const basePoints = parseInt(form.basePoints, 10);
    const multiplier = parseFloat(form.multiplier);
    if (!skipPolicy && (isNaN(basePoints) || basePoints < 0)) {
      setFormError(t('basePointsInvalid'));
      return;
    }
    if (!skipPolicy && (isNaN(multiplier) || multiplier < 0)) {
      setFormError(t('multiplierInvalid'));
      return;
    }

    if (editingId) {
      updateMutation.mutate({
        id: editingId,
        data: {
          displayName: form.displayName.trim(),
          color: form.color,
          sortOrder: parseInt(form.sortOrder, 10) || 0,
          ...(skipPolicy ? {} : { basePoints, multiplier: multiplier.toFixed(2) }),
          isDefault: form.isDefault,
          isZCustomer: form.isZCustomer,
        },
      });
    } else {
      if (!form.code.trim()) {
        setFormError(t('levelCodeEmpty'));
        return;
      }
      createMutation.mutate({
        code: form.code.trim().toUpperCase(),
        displayName: form.displayName.trim(),
        color: form.color,
        sortOrder: parseInt(form.sortOrder, 10) || 0,
        basePoints,
        multiplier: multiplier.toFixed(2),
        isDefault: form.isDefault,
        isZCustomer: form.isZCustomer,
      });
    }
  }

  const isSaving = createMutation.isPending || updateMutation.isPending;

  if (isLoading) {
    return (
      <AdminPage header={<AdminPageHeader title={t('tierManagement')} />}>
        {' '}
        <div className="p-6 text-base-content/40">{tc('loading')}</div>{' '}
      </AdminPage>
    );
  }

  return (
    <AdminPage
      contentClassName="admin-content-form"
      header={
        <AdminPageHeader
          title={t('tierManagement')}
          description={t('tierManagementDesc')}
          actions={
            <>
              {!showForm && (
                <button
                  onClick={startCreate}
                  className="min-h-11 px-4 py-2 rounded-lg bg-primary text-primary-content text-sm font-medium hover:bg-primary/90 transition-colors"
                >
                  {t('createLevel')}
                </button>
              )}
            </>
          }
        />
      }
    >
      <MemberLevelTransferPanel
        onImported={() => {
          void queryClient.invalidateQueries({ queryKey: ['member-levels'] });
        }}
      />
      {formError && !showForm && (
        <div className="mb-4 px-4 py-3 bg-error/10 border border-error/30 rounded-lg text-sm text-error">
          {formError}
        </div>
      )}

      {/* Create / Edit Form */}
      {showForm && (
        <div className="bg-base-200 border border-base-300 rounded-xl p-5 mb-6">
          <h2 className="text-lg font-semibold text-base-content mb-4">
            {editingId ? t('editLevel') : t('createLevel')}
          </h2>
          <div className="grid grid-cols-2 gap-4 mb-4">
            <div>
              <label className="block text-xs text-base-content/60 mb-1">
                {editingId ? t('levelCodeReadonly') : t('levelCodeLabel')}
              </label>
              <input
                type="text"
                value={form.code}
                onChange={(e) => setForm((f) => ({ ...f, code: e.target.value }))}
                disabled={!!editingId}
                placeholder={t('levelCodePlaceholder')}
                className="w-full px-3 py-2 rounded-lg border border-base-300 bg-base-100 text-base-content text-sm focus:outline-hidden focus:ring-2 focus:ring-primary/40 disabled:opacity-50"
              />
            </div>
            <div>
              <label className="block text-xs text-base-content/60 mb-1">
                {t('displayNameLabel')}
              </label>
              <input
                type="text"
                value={form.displayName}
                onChange={(e) => setForm((f) => ({ ...f, displayName: e.target.value }))}
                placeholder={t('displayNamePlaceholder')}
                className="w-full px-3 py-2 rounded-lg border border-base-300 bg-base-100 text-base-content text-sm focus:outline-hidden focus:ring-2 focus:ring-primary/40"
              />
            </div>
            <div>
              <label className="block text-xs text-base-content/60 mb-1">
                {t('badgeColorLabel')}
              </label>
              <div className="flex items-center gap-2">
                <input
                  type="color"
                  value={form.color}
                  onChange={(e) => setForm((f) => ({ ...f, color: e.target.value }))}
                  className="w-10 h-10 rounded border border-base-300 cursor-pointer"
                />
                <input
                  type="text"
                  value={form.color}
                  onChange={(e) => setForm((f) => ({ ...f, color: e.target.value }))}
                  className="flex-1 px-3 py-2 rounded-lg border border-base-300 bg-base-100 text-base-content text-sm focus:outline-hidden focus:ring-2 focus:ring-primary/40 font-mono"
                />
              </div>
            </div>
            <div>
              <label className="block text-xs text-base-content/60 mb-1">
                {t('sortOrderLabel')}
              </label>
              <input
                type="number"
                disabled={!!editingId && levels.some((l) => l.associationMode)}
                value={form.sortOrder}
                onChange={(e) => setForm((f) => ({ ...f, sortOrder: e.target.value }))}
                className="w-full px-3 py-2 rounded-lg border border-base-300 bg-base-100 text-base-content text-sm focus:outline-hidden focus:ring-2 focus:ring-primary/40"
              />
            </div>
            <div>
              <label className="block text-xs text-base-content/60 mb-1">
                {t('basePointsLabel')}
              </label>
              <input
                type="number"
                min="0"
                value={form.basePoints}
                onChange={(e) => setForm((f) => ({ ...f, basePoints: e.target.value }))}
                className="w-full px-3 py-2 rounded-lg border border-base-300 bg-base-100 text-base-content text-sm focus:outline-hidden focus:ring-2 focus:ring-primary/40"
              />
            </div>
            <div>
              <label className="block text-xs text-base-content/60 mb-1">
                {t('multiplierLabel')}
              </label>
              <input
                type="number"
                min="0"
                step="0.1"
                value={form.multiplier}
                onChange={(e) => setForm((f) => ({ ...f, multiplier: e.target.value }))}
                className="w-full px-3 py-2 rounded-lg border border-base-300 bg-base-100 text-base-content text-sm focus:outline-hidden focus:ring-2 focus:ring-primary/40"
              />
            </div>
          </div>
          <div className="flex items-center gap-2 mb-4">
            <input
              type="checkbox"
              disabled={levels.some((l) => l.associationMode)}
              id="isDefault"
              checked={form.isDefault}
              onChange={(e) => setForm((f) => ({ ...f, isDefault: e.target.checked }))}
              className="w-4 h-4"
            />
            <label htmlFor="isDefault" className="text-sm text-base-content/70">
              {t('setDefaultLevel')}
            </label>
          </div>
          <div className="flex items-center gap-2 mb-4">
            <input
              type="checkbox"
              disabled={levels.some((l) => l.associationMode)}
              id="isZCustomer"
              checked={form.isZCustomer}
              onChange={(e) => setForm((f) => ({ ...f, isZCustomer: e.target.checked }))}
              className="w-4 h-4"
            />
            <label htmlFor="isZCustomer" className="text-sm text-base-content/70">
              {t('setZCustomerLevel')}
            </label>
          </div>

          {formError && <p className="text-sm text-error mb-3">{formError}</p>}

          <div className="flex gap-2">
            <button
              onClick={handleSave}
              disabled={isSaving}
              className="px-4 py-2 text-sm rounded-lg bg-primary text-primary-content hover:bg-primary/90 transition-colors disabled:opacity-50"
            >
              {isSaving ? tc('loading') : editingId ? tc('save') : tc('create')}
            </button>
            <button
              onClick={resetForm}
              disabled={isSaving}
              className="px-4 py-2 text-sm rounded-lg bg-base-300 text-base-content/70 hover:bg-base-300/80 transition-colors"
            >
              {tc('cancel')}
            </button>
          </div>
        </div>
      )}

      {levels.some((level) => level.associationMode) && <MemberPolicyRepair />}
      {levels.some((level) => level.basePoints == null) && <p>{t('adminTools.missingPolicy')}</p>}
      {/* Level Cards */}
      <div className="space-y-3">
        {levels
          .filter((l) => l.isActive)
          .map((level) => {
            const actualPoints =
              level.basePoints == null || level.multiplier == null
                ? '未設定館別政策'
                : Math.round(level.basePoints * parseFloat(level.multiplier));
            return (
              <div key={level.id} className="bg-base-200 border border-base-300 rounded-xl p-5">
                <div className="flex items-center justify-between mb-3">
                  <div className="flex items-center gap-3">
                    <span
                      className="px-3 py-1 rounded-full text-sm font-medium text-white"
                      style={{ backgroundColor: level.color }}
                    >
                      {level.displayName}
                    </span>
                    <span className="text-xs text-base-content/40 font-mono">{level.code}</span>
                    {level.isDefault && (
                      <span className="text-xs bg-primary/10 text-primary px-2 py-0.5 rounded-full">
                        {t('defaultLabel')}
                      </span>
                    )}
                    {level.isZCustomer && (
                      <span className="text-xs bg-warning/10 text-warning px-2 py-0.5 rounded-full">
                        {t('zCustomerTag')}
                      </span>
                    )}
                  </div>
                  <div className="flex gap-2">
                    <button
                      onClick={() => startEdit(level)}
                      className="px-3 py-1.5 text-sm rounded-lg bg-base-300 hover:bg-primary/10 hover:text-primary transition-colors"
                    >
                      {tc('edit')}
                    </button>
                    {!level.isDefault &&
                      (deleteConfirm === level.id ? (
                        <div className="flex gap-1">
                          <button
                            onClick={() => deleteMutation.mutate(level.id)}
                            className="px-3 py-1.5 text-sm rounded-lg bg-error text-error-content hover:bg-error/90 transition-colors"
                          >
                            {t('confirmDelete')}
                          </button>
                          <button
                            onClick={() => setDeleteConfirm(null)}
                            className="px-3 py-1.5 text-sm rounded-lg bg-base-300 hover:bg-base-300/80 transition-colors"
                          >
                            {tc('cancel')}
                          </button>
                        </div>
                      ) : (
                        <button
                          onClick={() => setDeleteConfirm(level.id)}
                          className="px-3 py-1.5 text-sm rounded-lg bg-base-300 text-error hover:bg-error/10 transition-colors"
                        >
                          {tc('delete')}
                        </button>
                      ))}
                  </div>
                </div>
                <div className="flex gap-8">
                  <div className="text-center">
                    <p className="text-xs text-base-content/50 mb-1">{t('basePointsLabel')}</p>
                    <p className="text-xl font-bold text-base-content">
                      {level.basePoints ?? '未設定'}
                    </p>
                  </div>
                  <div className="text-center">
                    <p className="text-xs text-base-content/50 mb-1">{t('multiplier')}</p>
                    <p className="text-xl font-bold text-primary">
                      {level.multiplier == null ? '未設定' : `${parseFloat(level.multiplier)}x`}
                    </p>
                  </div>
                  <div className="text-center">
                    <p className="text-xs text-base-content/50 mb-1">{t('actualPoints')}</p>
                    <p className="text-xl font-bold text-emerald-600">{actualPoints}</p>
                  </div>
                  <div className="text-center">
                    <p className="text-xs text-base-content/50 mb-1">{t('sortOrderLabel')}</p>
                    <p className="text-xl font-bold text-base-content/60">{level.sortOrder}</p>
                  </div>
                </div>
              </div>
            );
          })}

        {/* Inactive levels */}
        {levels.filter((l) => !l.isActive).length > 0 && (
          <div className="mt-6">
            <h3 className="text-sm text-base-content/40 mb-2">{t('inactiveLevels')}</h3>
            {levels
              .filter((l) => !l.isActive)
              .map((level) => (
                <div
                  key={level.id}
                  className="bg-base-200/50 border border-base-300/50 rounded-xl p-4 opacity-60 mb-2"
                >
                  <div className="flex items-center gap-3">
                    <span
                      className="px-3 py-1 rounded-full text-sm font-medium text-white/70"
                      style={{ backgroundColor: level.color }}
                    >
                      {level.displayName}
                    </span>
                    <span className="text-xs text-base-content/30 font-mono">{level.code}</span>
                    <span className="text-xs text-base-content/30">{t('inactiveTag')}</span>
                  </div>
                </div>
              ))}
          </div>
        )}
      </div>

      <div className="mt-6 p-4 bg-base-300/50 rounded-lg text-sm text-base-content/50">
        {t('pointsFormula')}
      </div>

      <PointTypeCatalog />
    </AdminPage>
  );
}
